import type { Category } from '../scoring/types';
import type { MonthlyBest, MonthlySummary, Workout } from './types';

/** Category -> the MonthlySummary field holding that category's best. */
const BEST_FIELD: Record<
  Category,
  keyof Pick<MonthlySummary, 'pushBest' | 'rowBest' | 'hingeBest' | 'squatBest'>
> = {
  push: 'pushBest',
  row: 'rowBest',
  hinge: 'hingeBest',
  squat: 'squatBest',
};

export function toBest(w: Workout): MonthlyBest {
  return {
    category: w.category,
    workoutId: w.id,
    points: w.points,
    normalized1RmLb: w.normalized1RmLb,
    estimated1RmLb: w.estimated1RmLb,
    exerciseId: w.exerciseId,
    exerciseName: w.exerciseNameSnapshot,
    enteredWeightLb: w.enteredWeightLb,
    reps: w.reps,
    inputWeightMultiplier: w.inputWeightMultiplier,
    bodyWeightFactor: w.bodyWeightFactor,
  };
}

/**
 * The highest-scoring lift per exercise, keyed by exercise id. Powers the
 * per-lift leaderboards, and comes off the same workouts the summary already
 * walks so no extra query is needed.
 */
export function buildExerciseBests(workouts: Workout[]): Record<string, MonthlyBest> {
  const bests: Record<string, MonthlyBest> = {};
  for (const w of workouts) {
    if (!w.exerciseId) continue;
    const current = bests[w.exerciseId];
    if (!current || w.points > current.points) bests[w.exerciseId] = toBest(w);
  }
  return bests;
}

/**
 * Reduces a month's workouts to the highest-scoring lift per category, plus the
 * monthly total. Shared by the repositories and the history/trend hooks so the
 * category list only ever has to be extended in one place.
 */
export function buildMonthlySummary(workouts: Workout[]): MonthlySummary {
  const summary: MonthlySummary = {
    pushBest: null,
    rowBest: null,
    hingeBest: null,
    squatBest: null,
    totalPoints: 0,
    isIncomplete: true,
  };

  for (const w of workouts) {
    const field = BEST_FIELD[w.category];
    if (!field) continue; // unknown/legacy category - ignore rather than crash
    const current = summary[field];
    if (!current || w.points > current.points) {
      summary[field] = toBest(w);
    }
  }

  const bests = [summary.pushBest, summary.rowBest, summary.hingeBest, summary.squatBest];
  summary.totalPoints = bests.reduce((sum, b) => sum + (b?.points ?? 0), 0);
  summary.isIncomplete = bests.some((b) => !b);

  return summary;
}
